import React, { useState } from "react";
import { 
  AlertCircle, 
  Target, 
  CheckCircle, 
  MapPin, 
  Calendar, 
  Clock,
  Zap,
  X
} from "lucide-react";

const BulkOperations = ({ selectedSchedule, availableGrounds, schedulesService, onScheduleUpdate, onClose }) => {
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [actionValues, setActionValues] = useState({
    venue: "",
    date: "",
    time: ""
  });
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const toggleAction = (action) => {
    setSelectedActions((prev) => {
      const s = new Set(prev);
      s.has(action) ? s.delete(action) : s.add(action);
      return s;
    });
  };

  const toggleWeek = (wi) => {
    setSelectedWeeks((prev) => {
      const s = new Set(prev);
      s.has(wi) ? s.delete(wi) : s.add(wi);
      return s;
    });
  };

  const selectAllWeeks = () => {
    const allWeeks = new Set(selectedSchedule.schedule_data.map((_, i) => i));
    setSelectedWeeks(allWeeks);
  };

  const clearAllWeeks = () => {
    setSelectedWeeks(new Set());
  };

  const handleApply = async () => {
    if (selectedActions.size === 0 || selectedWeeks.size === 0) {
      setError("Please select at least one action and one week");
      return;
    }

    // Validate that selected actions have values
    for (const action of selectedActions) {
      if (!actionValues[action]) {
        setError(`Please provide a value for ${action}`);
        return;
      }
    }

    try {
      setSaving(true);
      setError("");
      const updated = [...selectedSchedule.schedule_data];
      
      selectedWeeks.forEach((wi) => {
        updated[wi].games = updated[wi].games.map((g) => {
          let updatedGame = { ...g };
          
          selectedActions.forEach((action) => {
if (action === "venue") {
  const ground = availableGrounds.find((gr) => gr.id === actionValues.venue);
  if (ground) {
    updatedGame = {
      ...updatedGame,
      ground_id: ground.id, 
      ground_name: ground.name || ground.ground_name,
      ground_city: ground.city,
      ground_state: ground.state,
      ground_address_1: ground.address_1,
      ground_address_2: ground.address_2,
      ground_zip_code: ground.zip_code,
      ground_country: ground.country
    };
  }
}
            if (action === "time") {
              updatedGame = { ...updatedGame, time: actionValues.time };
            }
            if (action === "date") {
              updatedGame = { ...updatedGame, date: actionValues.date };
            }
          });
          
          return updatedGame;
        });
      });

      await schedulesService.update(selectedSchedule.id, { 
        gameEdit: true,
        scheduleData: updated 
      });
      
      // Show success message
      setShowSuccessMessage(true);
      
      // CRITICAL: Wait for database to fully commit
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Refresh parent data
      await onScheduleUpdate();
      
      // Reset form
      setSelectedWeeks(new Set());
      setSelectedActions(new Set());
      setActionValues({ venue: "", date: "", time: "" });
      
      setTimeout(() => setShowSuccessMessage(false), 2000);
    } catch (err) {
      setError("Failed to apply bulk changes");
    } finally {
      setSaving(false);
    }
  };

  const totalGamesAffected = Array.from(selectedWeeks).reduce((sum, wi) => {
    return sum + (selectedSchedule.schedule_data[wi]?.games.length || 0);
  }, 0);

  return (
    <div style={{ 
      width: '100%', 
      height: '600px', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      position: 'relative'
    }}>
      {/* Success Message */}
      {showSuccessMessage && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: '500' }}>Bulk changes applied successfully!</span>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Zap size={24} color="#f59e0b" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Bulk Operations</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {selectedWeeks.size > 0 && (
            <div style={{
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {selectedWeeks.size} weeks • {totalGamesAffected} games
            </div>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
              color: '#6b7280'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Instruction */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#92400e',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <AlertCircle size={16} />
        <span>Apply the same change to multiple weeks at once</span>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          margin: '16px 20px 0',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626',
          fontSize: '14px'
        }}>
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Content - Scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
      }}>
        {/* Action Selection */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>
            Select Actions (one or more)
          </label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px'
          }}>
            {[
              { value: 'venue', label: 'Set Venue', icon: MapPin, color: '#10b981' },
              { value: 'date', label: 'Set Date', icon: Calendar, color: '#3b82f6' },
              { value: 'time', label: 'Set Time', icon: Clock, color: '#f59e0b' }
            ].map((action) => {
              const Icon = action.icon;
              const isSelected = selectedActions.has(action.value);
              return (
                <button
                  key={action.value}
                  onClick={() => toggleAction(action.value)}
                  style={{
                    padding: '12px',
                    border: isSelected ? `2px solid ${action.color}` : '2px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: isSelected ? `${action.color}15` : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    fontWeight: isSelected ? '600' : '500',
                    color: isSelected ? action.color : '#6b7280',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: action.color,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <CheckCircle size={14} color="white" />
                    </div>
                  )}
                  <Icon size={24} />
                  <span style={{ fontSize: '14px' }}>{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Value Inputs for Selected Actions */}
        {selectedActions.size > 0 && (
          <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {selectedActions.has('venue') && (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <MapPin size={18} />
                  Select Venue
                </label>
                <select
                  value={actionValues.venue}
                  onChange={(e) => setActionValues(prev => ({ ...prev, venue: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a venue...</option>
                  {availableGrounds.map((gr) => (
                    <option key={gr.id} value={gr.id}>
                      {gr.name} - {gr.city}, {gr.state}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedActions.has('date') && (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Calendar size={18} />
                  Select Date
                </label>
                <input
                  type="date"
                  value={actionValues.date}
                  onChange={(e) => setActionValues(prev => ({ ...prev, date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            {selectedActions.has('time') && (
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Clock size={18} />
                  Select Time
                </label>
                <input
                  type="time"
                  value={actionValues.time}
                  onChange={(e) => setActionValues(prev => ({ ...prev, time: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Week Selection */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Select Weeks to Apply
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={selectAllWeeks}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: '#3b82f6'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                Select All
              </button>
              <button
                onClick={clearAllWeeks}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                Clear All
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '10px',
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            {selectedSchedule.schedule_data.map((w, wi) => {
              const isSelected = selectedWeeks.has(wi);
              return (
                <label
                  key={wi}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    backgroundColor: isSelected ? '#dbeafe' : 'white',
                    border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontWeight: isSelected ? '600' : '500',
                    color: isSelected ? '#1e40af' : '#374151'
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleWeek(wi)}
                    style={{
                      margin: 0,
                      transform: 'scale(1.1)',
                      accentColor: '#3b82f6',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ fontSize: '14px' }}>
                    <div>Week {w.week}</div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: isSelected ? '#60a5fa' : '#9ca3af',
                      marginTop: '2px'
                    }}>
                      {w.games.length} games
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        {selectedWeeks.size > 0 && selectedActions.size > 0 && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px'
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              color: '#1e40af',
              marginBottom: '8px'
            }}>
              Preview
            </div>
            <div style={{ fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
              Will update <strong>{totalGamesAffected} games</strong> across{' '}
              <strong>{selectedWeeks.size} week{selectedWeeks.size > 1 ? 's' : ''}</strong>:
              <ul style={{ marginTop: '8px', marginBottom: '0', paddingLeft: '20px' }}>
                {selectedActions.has('venue') && actionValues.venue && (
                  <li>
                    Venue: <strong>
                      {availableGrounds.find(g => g.id === actionValues.venue)?.name || 'Unknown'}
                    </strong>
                  </li>
                )}
                {selectedActions.has('date') && actionValues.date && (
                  <li>
                    Date: <strong>{new Date(actionValues.date + 'T00:00:00').toLocaleDateString()}</strong>
                  </li>
                )}
                {selectedActions.has('time') && actionValues.time && (
                  <li>
                    Time: <strong>{actionValues.time}</strong>
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Footer with Apply Button */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <button
          disabled={saving || selectedActions.size === 0 || selectedWeeks.size === 0 || !Array.from(selectedActions).every(action => actionValues[action])}
          onClick={handleApply}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: (saving || selectedActions.size === 0 || selectedWeeks.size === 0 || !Array.from(selectedActions).every(action => actionValues[action])) ? 'not-allowed' : 'pointer',
            backgroundColor: (saving || selectedActions.size === 0 || selectedWeeks.size === 0 || !Array.from(selectedActions).every(action => actionValues[action])) ? '#93c5fd' : '#3b82f6',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            opacity: (saving || selectedActions.size === 0 || selectedWeeks.size === 0 || !Array.from(selectedActions).every(action => actionValues[action])) ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!saving && selectedActions.size > 0 && selectedWeeks.size > 0 && Array.from(selectedActions).every(action => actionValues[action])) {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!saving && selectedActions.size > 0 && selectedWeeks.size > 0 && Array.from(selectedActions).every(action => actionValues[action])) {
              e.target.style.backgroundColor = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          {saving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Applying...
            </>
          ) : (
            <>
              <Target size={18} />
              Apply to {selectedWeeks.size} Week{selectedWeeks.size > 1 ? 's' : ''}
            </>
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BulkOperations;